type LogLevel = 'debug' | 'info' | 'warn' | 'error';

type LogMetadata = Record<string, unknown>;

type LogDetails = {
  scope?: string;
  error?: unknown;
  metadata?: LogMetadata;
};

export type MonitoringEvent = {
  level: LogLevel;
  message: string;
  scope?: string;
  error?: unknown;
  metadata?: LogMetadata;
  timestamp: string;
};

export type MonitoringSink = (event: MonitoringEvent) => void;

const DEFAULT_SCOPE = 'app';
const PROD_LOG_LEVELS: LogLevel[] = ['warn', 'error'];

let monitoringSink: MonitoringSink | undefined;

const canWriteToConsole = (level: LogLevel) => {
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }
  return PROD_LOG_LEVELS.includes(level);
};

const writeToConsole = (
  level: LogLevel,
  message: string,
  details: LogDetails = {},
) => {
  if (!canWriteToConsole(level)) {
    return;
  }

  const scope = details.scope ?? DEFAULT_SCOPE;
  const metadata = details.metadata ? { metadata: details.metadata } : {};
  const payload = {
    scope,
    ...metadata,
  };

  switch (level) {
    case 'debug':
      console.debug(message, payload, details.error);
      return;
    case 'info':
      console.info(message, payload, details.error);
      return;
    case 'warn':
      console.warn(message, payload, details.error);
      return;
    case 'error':
      console.error(message, payload, details.error);
      return;
    default:
      console.log(message, payload, details.error);
  }
};

const writeToMonitoringSink = (
  level: LogLevel,
  message: string,
  details: LogDetails = {},
) => {
  if (!monitoringSink) {
    return;
  }

  monitoringSink({
    level,
    message,
    scope: details.scope ?? DEFAULT_SCOPE,
    error: details.error,
    metadata: details.metadata,
    timestamp: new Date().toISOString(),
  });
};

const writeLog = (level: LogLevel, message: string, details?: LogDetails) => {
  writeToConsole(level, message, details);
  writeToMonitoringSink(level, message, details);
};

export const setMonitoringSink = (sink?: MonitoringSink) => {
  monitoringSink = sink;
};

export const logger = {
  debug: (message: string, details?: LogDetails) =>
    writeLog('debug', message, details),
  info: (message: string, details?: LogDetails) =>
    writeLog('info', message, details),
  warn: (message: string, details?: LogDetails) =>
    writeLog('warn', message, details),
  error: (message: string, details?: LogDetails) =>
    writeLog('error', message, details),
};
